// @ts-nocheck
/**
 * One swipe deck: the recipe card on top, the next one peeking under it, drag and fly-out, tap to pause,
 * the sound button. Markup: snippets/vhp-swipe-deck.liquid, styles: assets/vhp-collection-reels.css.
 * Used by the swipe feed (assets/vhp-collection-reels.js) and the planner's setup deck
 * (assets/vhp-planner-funnel.js). They decide which card comes next and what a swipe means; this file only
 * paints the cards and turns gestures into onSwipe('take' | 'skip').
 *
 *   const deck = window.vhpSwipeDeck.create(root, {
 *     canDrag(),          // a drag or tap may start on the deck (default: always)
 *     canSwipe(),         // a fly-out may run now (buttons and released drags)
 *     onSwipe(kind),      // after the fly-out: 'take' | 'skip'
 *     canTap(),           // a short tap may pause / play the video (default: always)
 *     lift: { canLift(), onCommit() },  // swipe up opens the recipe (feed only)
 *     dirEls: [el],       // get data-dir="take|skip" while dragging (default: [root])
 *     ignore: 'selector', // more elements that never start a drag
 *     badge(el, card),    // fills the meal badge (default: card.badge text)
 *     face(ns, card),     // extra face text; ns is 'swipe' (top card) or 'swipe-under' (peek)
 *     poster(card),       // poster URL (default: card.image)
 *   });
 *   deck.show(card, { force })  // top card; same card again is a no-op unless force. Returns whether it filled.
 *   deck.showNext(card | null)  // card peeking under it (its video preloads once the top one plays)
 *   deck.useCard(el | null)     // another element takes the card slot (feed: native cards); null = the recipe card
 *   deck.flyOut(kind), deck.reset(), deck.pause(), deck.dragging, deck.current, deck.card, deck.realCard
 *
 * A card is { id, title, slot, protein, kcal, time, image, exclusive?, el } where el is the recipe card in
 * the page (snippets/vhp-recipe-card.liquid) whose <deferred-media> template holds the video.
 */
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpSwipeDeck) return;

  const DRAG_COMMIT = 90;
  const LIFT_COMMIT = 70; // swipe up this far (px) to open the recipe
  const LIFT_MAX = 90;
  const MUTE_KEY = 'vhp-reel-muted';
  // Tap on the video (not the title, macros, buttons or links) pauses / plays it, like a reel. A paused video
  // stays paused when a sheet closes (vhp-swipe-week.js and the planner skip it); the next card plays again.
  const TAP_IGNORE =
    'a, button, input, .vhp-swipe-card__badge, .vhp-swipe-card__title, .vhp-swipe-card__macros, .vhp-swipe-card__more, .vhp-swipe-card__liked';
  const DRAG_IGNORE =
    '[data-vhp-reel-control], [data-vhp-swipe-recipe], [data-vhp-swipe-info], [data-vhp-reel-mute], [data-vhp-reel-share]';

  // Sound is one setting for every deck and the whole visit; videos start muted.
  let muted = (() => {
    try {
      const saved = sessionStorage.getItem(MUTE_KEY);
      return saved === null ? true : saved === '1';
    } catch (_) {
      return true;
    }
  })();
  const decks = new Set();

  const fmt = (value) => {
    const n = Number(value);
    return value != null && value !== '' && Number.isFinite(n) ? String(Math.round(n)) : '–';
  };

  const pauseVideos = (root) => {
    root?.querySelectorAll('video').forEach((video) => {
      try {
        video.pause();
      } catch (_) {}
    });
  };

  const mediaFromCard = (card) => {
    const deferred = card?.el?.querySelector('deferred-media');
    const fromTemplate = deferred?.querySelector('template')?.content.cloneNode(true);
    const video = fromTemplate?.querySelector('video') || deferred?.querySelector('video')?.cloneNode(true) || null;
    if (video) {
      // The deck paints its own poster under the video; video_tag's 2500px poster attribute and its fallback
      // <img> (which browsers fetch once the video is in the page) would be ~300 KB on top of every video.
      video.removeAttribute('poster');
      video.querySelectorAll('img').forEach((img) => img.remove());
    }
    return { video, iframe: fromTemplate?.querySelector('iframe') || null };
  };

  const makePoster = (url) => {
    const poster = document.createElement('img');
    poster.className = 'vhp-swipe-card__poster';
    poster.src = url;
    poster.alt = '';
    poster.draggable = false;
    return poster;
  };

  const armVideo = (video, play, mute) => {
    video.muted = mute;
    video.defaultMuted = mute;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.preload = 'auto';
    video.autoplay = play;
    if (mute) video.setAttribute('muted', '');
    else video.removeAttribute('muted');
    video.setAttribute('playsinline', '');
    if (play) video.setAttribute('autoplay', '');
    else video.removeAttribute('autoplay');
  };

  const setMuted = (next) => {
    muted = Boolean(next);
    try {
      sessionStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    } catch (_) {}
    decks.forEach((deck) => deck.paintSound());
  };

  const create = (root, opts = {}) => {
    const q = (sel) => root.querySelector(sel);
    const deckEl = q('[data-vhp-swipe-deck]');
    const realCard = q('[data-vhp-swipe-card]');
    if (!deckEl || !realCard) return null;
    const media = q('[data-vhp-swipe-media]');
    const peek = q('[data-vhp-swipe-under]');
    const peekMedia = q('[data-vhp-swipe-under-media]');
    const dirEls = opts.dirEls || [root];
    const posterOf = (card) => (opts.poster ? opts.poster(card) : card.image) || '';

    let card = realCard;
    let current = null;
    let drag = null;
    let mediaGen = 0;
    let currentReady = false;
    let preloadedId = null;
    let pendingPeek = null;
    let peekTimer = 0;

    const fillFace = (ns, data) => {
      const badge = q(`[data-vhp-${ns}-badge]`);
      if (badge) {
        if (opts.badge) opts.badge(badge, data);
        else badge.textContent = data.badge || '';
      }
      const exclusive = q(`[data-vhp-${ns}-exclusive]`);
      if (exclusive) exclusive.hidden = !data.exclusive;
      const title = q(`[data-vhp-${ns}-title]`);
      if (title) {
        title.textContent = data.title || '';
        title.style.setProperty('--vhp-title-len', Math.max(title.textContent.length, 1));
      }
      const protein = q(`[data-vhp-${ns}-protein]`);
      if (protein) protein.textContent = `${fmt(data.protein)}g`;
      const kcal = q(`[data-vhp-${ns}-kcal]`);
      if (kcal) kcal.textContent = fmt(data.kcal);
      const timeWrap = q(`[data-vhp-${ns}-time-wrap]`);
      if (timeWrap) timeWrap.hidden = data.time == null;
      const time = q(`[data-vhp-${ns}-time]`);
      if (time) time.textContent = fmt(data.time);
      opts.face?.(ns, data);
    };

    const paintSound = () => {
      root.querySelectorAll('[data-vhp-reel-mute]').forEach((btn) => {
        btn.setAttribute('data-state', muted ? 'muted' : 'unmuted');
        btn.setAttribute('aria-label', muted ? tr('common.sound_on') : tr('common.sound_off'));
      });
      media?.querySelectorAll('video').forEach((video) => {
        video.muted = muted;
        if (muted) video.setAttribute('muted', '');
        else video.removeAttribute('muted');
      });
    };

    const takePreloaded = (id) => {
      if (preloadedId !== id) return null;
      const video = peekMedia?.querySelector('video');
      preloadedId = null;
      if (warming === video) warming = null;
      return video || null;
    };

    // Mobile Safari ignores preload on a video that is not playing, so the peek would only ever show the poster.
    // A muted play() that pauses straight away makes every browser buffer and decode frame 0; the card under the
    // top one then shows the real first frame and the swipe continues from it instead of from the poster.
    let warming = null;

    const preloadIntoPeek = (next) => {
      if (!peekMedia || !next) return;
      if (preloadedId === next.id && peekMedia.querySelector('video')) return;
      peekMedia.querySelectorAll('video').forEach((v) => v.remove());
      preloadedId = null;
      warming = null;
      const { video } = mediaFromCard(next);
      if (!video) return;
      armVideo(video, false, true);
      video.style.opacity = '0';
      video.addEventListener(
        'loadeddata',
        () => {
          if (preloadedId === next.id) video.style.opacity = '1';
        },
        { once: true }
      );
      peekMedia.append(video);
      preloadedId = next.id;
      warming = video;
      video.addEventListener(
        'playing',
        () => {
          if (warming !== video) return;
          warming = null;
          video.pause();
          try {
            video.currentTime = 0;
          } catch (_) {}
        },
        { once: true }
      );
      const play = video.play();
      if (play && typeof play.catch === 'function') {
        play.catch(() => {
          // Autoplay blocked (iOS Low Power Mode): plain preload is all we get.
          if (warming !== video) return;
          warming = null;
          try {
            video.load();
          } catch (_) {}
        });
      }
    };

    // The swipe is committed: the card underneath starts playing while the top one flies out, like a reel.
    const startPeek = () => {
      const video = preloadedId && peek?.dataset.id === preloadedId ? peekMedia?.querySelector('video') : null;
      if (!video) return null;
      warming = null;
      video.play()?.catch?.(() => {});
      return video;
    };

    const runPendingPeek = () => {
      window.clearTimeout(peekTimer);
      const next = pendingPeek;
      pendingPeek = null;
      if (next && peek?.dataset.id === next.id) preloadIntoPeek(next);
    };

    // The next card's video only starts downloading once the visible one plays (or after 1.5s), so the
    // first video of a visit gets the whole connection.
    const preloadPeekWhenFree = (next) => {
      window.clearTimeout(peekTimer);
      pendingPeek = null;
      if (currentReady) {
        preloadIntoPeek(next);
        return;
      }
      pendingPeek = next;
      peekTimer = window.setTimeout(runPendingPeek, 1500);
    };

    const showNext = (next) => {
      if (!peek || !peekMedia) return;
      if (!next) {
        peek.hidden = true;
        peek.style.setProperty('--promote', '0');
        peek.removeAttribute('data-id');
        pauseVideos(peekMedia);
        peekMedia.replaceChildren();
        preloadedId = null;
        return;
      }
      peek.hidden = false;
      fillFace('swipe-under', next);
      if (peek.dataset.id === next.id) {
        preloadPeekWhenFree(next);
        return;
      }
      peek.dataset.id = next.id;
      pauseVideos(peekMedia);
      peekMedia.replaceChildren();
      preloadedId = null;
      const url = posterOf(next);
      if (url) peekMedia.append(makePoster(url));
      preloadPeekWhenFree(next);
    };

    const show = (next, { force = false } = {}) => {
      if (!next) return false;
      if (!force && current?.id === next.id && realCard.dataset.id === next.id) return false;
      current = next;
      realCard.dataset.id = next.id;
      const gen = ++mediaGen;
      pauseVideos(media);
      media.replaceChildren();
      realCard.classList.add('is-media-loading');
      realCard.classList.remove('is-user-paused');
      currentReady = false;

      const url = posterOf(next);
      if (url) {
        const poster = makePoster(url);
        poster.fetchPriority = 'high';
        media.append(poster);
      }

      let video = takePreloaded(next.id);
      let iframe = null;
      if (!video) ({ video, iframe } = mediaFromCard(next));

      const ready = () => {
        if (gen !== mediaGen) return;
        realCard.classList.remove('is-media-loading');
        currentReady = true;
        runPendingPeek();
      };

      root.setAttribute('data-has-video', video ? 'true' : 'false');
      if (video) {
        armVideo(video, true, muted);
        video.style.opacity = '';
        media.append(video);
        video.addEventListener('playing', ready, { once: true });
        video.addEventListener('error', ready, { once: true });
        // Already playing since the fly-out: no new 'playing' event, the play() promise settles right away.
        const play = video.play();
        if (play && typeof play.then === 'function') play.then(ready, ready);
        window.setTimeout(ready, 5000);
      } else if (iframe) {
        media.append(iframe);
        iframe.addEventListener('load', ready, { once: true });
        window.setTimeout(ready, 4000);
      } else {
        ready();
      }

      fillFace('swipe', next);
      paintSound();
      // Desktop stage: this photo glows around the phone column (assets/app-shell.js).
      if (url) root.dispatchEvent(new CustomEvent('vhp:deck-show', { bubbles: true, detail: { image: url } }));
      return true;
    };

    const setDrag = (dx) => {
      const p = Math.max(-1, Math.min(1, dx / 120));
      const fade = Math.min(1, Math.abs(dx) / 180);
      card.style.setProperty('--drag', String(p));
      card.style.transform = dx ? `translateX(${dx}px) rotate(${dx / 16}deg)` : '';
      card.style.opacity = dx ? String(1 - fade * 0.85) : '';
      peek?.style.setProperty('--promote', String(fade));
      const dir = p > 0.25 ? 'take' : p < -0.25 ? 'skip' : '';
      dirEls.forEach((el) => {
        if (el) el.dataset.dir = dir;
      });
    };

    const reset = () => {
      card.style.transition = '';
      card.style.opacity = '';
      setDrag(0);
    };

    // Swipe up: the card follows the finger a little and "Zutaten & Schritte" fades in.
    const setLift = (dy) => {
      const lift = Math.max(-LIFT_MAX, Math.min(0, dy * 0.45));
      card.style.transform = lift ? `translateY(${lift}px)` : '';
      card.style.setProperty('--lift', String(Math.min(1, Math.abs(dy) / LIFT_COMMIT)));
    };

    const flyOut = (kind) => {
      if (card.hidden || (opts.canSwipe && !opts.canSwipe())) return;
      card.classList.remove('is-hinting');
      const dir = kind === 'take' ? 1 : -1;
      card.style.transition = 'transform 0.32s ease, opacity 0.32s ease';
      if (peek) peek.style.transition = 'transform 0.32s ease';
      setDrag(dir * 160);
      card.style.transform = `translateX(${dir * 70}%) rotate(${dir * 14}deg)`;
      card.style.opacity = '0';
      peek?.style.setProperty('--promote', '1');
      const started = card === realCard ? startPeek() : null;
      window.setTimeout(() => {
        card.style.transition = 'none';
        if (peek) peek.style.transition = 'none';
        try {
          opts.onSwipe?.(kind);
        } finally {
          reset();
          // The swipe did not promote that card (gate, sheet): it waits under the top one again.
          if (started && started.parentNode === peekMedia) started.pause();
        }
        window.requestAnimationFrame(() => {
          card.style.transition = '';
          if (peek) peek.style.transition = '';
        });
      }, 320);
    };

    const toggleTapPause = (target) => {
      if (!(target instanceof Element) || target.closest(TAP_IGNORE)) return;
      const video = media?.querySelector('video');
      if (!video) return;
      const pause = video.dataset.userPaused !== '1';
      realCard.classList.toggle('is-user-paused', pause);
      if (pause) {
        video.dataset.userPaused = '1';
        video.pause();
      } else {
        delete video.dataset.userPaused;
        video.play()?.catch?.(() => {});
      }
      window.vhpTrack?.('feed_video_toggle', { paused: pause });
    };

    deckEl.addEventListener('dragstart', (event) => event.preventDefault());

    deckEl.addEventListener('pointerdown', (event) => {
      if (event.button || card.hidden || (opts.canDrag && !opts.canDrag())) return;
      if (event.target.closest(DRAG_IGNORE) || (opts.ignore && event.target.closest(opts.ignore))) return;
      card.classList.remove('is-hinting');
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, at: Date.now(), active: false };
    });

    deckEl.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          // Swipe up opens the recipe where the deck allows it; anything else is left to the page (scrolling).
          if (!opts.lift || dy > 0 || (opts.lift.canLift && !opts.lift.canLift())) {
            drag = null;
            return;
          }
          drag.vertical = true;
        }
        drag.active = true;
        deckEl.setPointerCapture(event.pointerId);
        card.style.transition = 'none';
        if (peek) peek.style.transition = 'none';
      }
      if (event.cancelable) event.preventDefault();
      if (drag.vertical) setLift(dy);
      else setDrag(dx);
    });

    const endDrag = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const { active, x, y, at, vertical } = drag;
      drag = null;
      if (!active) {
        // A short tap (no long press) on the video.
        if (event.type === 'pointerup' && Date.now() - at < 500 && card === realCard && (!opts.canTap || opts.canTap())) {
          toggleTapPause(event.target);
        }
        return;
      }
      if (vertical) {
        const dy = event.type === 'pointercancel' ? 0 : event.clientY - y;
        card.style.transition = 'transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1)';
        setLift(0);
        if (dy < -LIFT_COMMIT) opts.lift.onCommit?.();
        return;
      }
      const dx = event.type === 'pointercancel' ? 0 : event.clientX - x;
      if (dx > DRAG_COMMIT) flyOut('take');
      else if (dx < -DRAG_COMMIT) flyOut('skip');
      else {
        card.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
        if (peek) peek.style.transition = 'transform 0.2s ease';
        setDrag(0);
      }
    };

    deckEl.addEventListener('pointerup', endDrag);
    deckEl.addEventListener('pointercancel', endDrag);

    root.addEventListener('click', (event) => {
      const btn = event.target instanceof Element && event.target.closest('[data-vhp-reel-mute]');
      if (!btn) return;
      event.preventDefault();
      event.stopPropagation();
      setMuted(!muted);
      // Sound on for a video that is not playing yet (autoplay blocked): start it with this tap.
      const video = media?.querySelector('video');
      if (!muted && video?.paused && video.dataset.userPaused !== '1') video.play()?.catch?.(() => {});
    });

    // Keyboard (desktop, snippets/app-sidepanel.liquid lists the keys): ← skip, → take, ↑ open the recipe, space
    // pauses. Only while the card is the thing on screen: a sheet or dialog over its middle wins. True = handled.
    let keyLock = 0;
    const onKey = (key) => {
      if (card.hidden || drag) return false;
      const box = card.getBoundingClientRect();
      if (!box.width || !box.height) return false;
      const hit = document.elementFromPoint(box.left + box.width / 2, box.top + box.height / 2);
      if (!hit || !root.contains(hit)) return false;
      if (Date.now() < keyLock) return true;
      if (key === 'ArrowRight' || key === 'ArrowLeft') {
        if (opts.canSwipe && !opts.canSwipe()) return false;
        keyLock = Date.now() + 380; // one card per press while the fly-out runs
        flyOut(key === 'ArrowRight' ? 'take' : 'skip');
        return true;
      }
      if (key === 'ArrowUp') {
        if (!opts.lift || (opts.lift.canLift && !opts.lift.canLift())) return false;
        opts.lift.onCommit?.();
        return true;
      }
      if (card !== realCard || (opts.canTap && !opts.canTap())) return false;
      toggleTapPause(realCard);
      return true;
    };

    const api = {
      root,
      deckEl,
      realCard,
      media,
      peek,
      get card() {
        return card;
      },
      get current() {
        return current;
      },
      get dragging() {
        return Boolean(drag);
      },
      show,
      showNext,
      flyOut,
      reset,
      paintSound,
      pause: () => pauseVideos(media),
      useCard(el) {
        card = el || realCard;
      },
      key: onKey,
    };
    decks.add(api);
    paintSound();
    return api;
  };

  const DECK_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', ' '];
  document.addEventListener('keydown', (event) => {
    if (event.defaultPrevented || event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
    if (!DECK_KEYS.includes(event.key)) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;
    // Space on a focused button or link is that control's own click.
    if (event.key === ' ' && target?.closest('button, a, [role="button"]')) return;
    for (const deck of decks) {
      if (deck.root.isConnected && deck.key(event.key)) {
        event.preventDefault();
        return;
      }
    }
  });

  window.vhpSwipeDeck = {
    create,
    get muted() {
      return muted;
    },
    setMuted,
  };
})();
